import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { HttpHelpService } from '../service/http-help.service';
import { companyLabelHeEnAr } from 'src/app/components/alldocuments/alldocuments-companies/companies.service';
import { getDefaultReportLanguage } from 'src/app/shared/services/report-language-preference';
import {
  UnistructJob,
  UnistructService,
  UnistructStep,
  buildUnistructSteps,
  isUnistructService401,
  readApiMessage,
  shouldPollUnistructJob,
} from 'src/app/services/unistruct.service';

type OauthLinkState = 'unknown' | 'missing' | 'expired';

@Component({
  selector: 'app-permanent-file',
  templateUrl: './permanent-file.component.html',
  styleUrls: ['./permanent-file.component.scss'],
})
export class PermanentFileComponent implements OnInit, OnDestroy {
  curentLang = '';
  layoutVersion: string | null = null;
  userRegistId: number | null = null;
  merchantId: number | null = null;
  mastercompany: any[] = [];
  createZip: FormGroup;
  taxRegister = '';

  oauthState: OauthLinkState = 'unknown';
  oauthAttention = false;
  linkDetail = '';
  connecting = false;

  submitting = false;
  downloading = false;
  actionError = '';

  jobs: UnistructJob[] = [];
  jobsLoading = false;
  jobsError = '';
  expandedJobId: number | null = null;
  private busyJobIds = new Set<number>();

  private readonly destroy$ = new Subject<void>();
  private readonly pollMs = 45000;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private jobsRequest = 0;
  private companyRequest = 0;
  private pollGeneration = 0;
  private pollingIds = new Set<number>();
  private jobEpoch = new Map<number, number>();
  private selectedCompanyId: number | null = null;

  constructor(
    public translate: TranslateService,
    private service: HttpHelpService,
    private unistruct: UnistructService,
  ) {
    this.curentLang = localStorage.getItem('curentLang') || 'en';
    this.createZip = new FormGroup({
      company_id: new FormControl(null, Validators.required),
      start_date: new FormControl('', Validators.required),
      end_date: new FormControl('', Validators.required),
    }, { validators: dateRangeValidator });
  }

  ngOnInit(): void {
    this.curentLang = localStorage.getItem('curentLang') || this.curentLang;
    this.userRegistId = readStorageNumber('user_id');
    this.merchantId = readStorageNumber('merchant_id');
    this.applyLayoutVersion();

    this.createZip.get('company_id')!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.onCompanyChange(value));

    this.loadCompanies();
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (typeof document !== 'undefined' && !document.hidden) {
      this.pollOnce();
    }
  }

  get companyId(): number | null {
    return this.selectedCompanyId;
  }

  getCompanyDisplayName(company: any): string {
    if (!company) {
      return '';
    }
    const langName =
      this.curentLang === 'en'
        ? company.company_name_en
        : this.curentLang === 'ar'
          ? company.company_name_ar
          : company.company_name_he;
    const trimmed = String(langName ?? '').trim();
    if (trimmed) {
      return trimmed;
    }
    return companyLabelHeEnAr(company) || String(company.company_name_en ?? '').trim();
  }

  downloadLocal(): void {
    if (!this.ensurePeriodReady()) {
      return;
    }
    const request = this.buildRequest(true);
    if (!request) {
      return;
    }

    this.downloading = true;
    this.actionError = '';
    this.unistruct.downloadZip(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: async (response) => {
        const blob = response.body;
        const contentType = response.headers.get('content-type') || blob?.type || '';
        if (!blob || !(await isZipBlob(blob, contentType))) {
          const message = blob ? readApiMessage(await blob.text(), this.curentLang) : '';
          this.downloading = false;
          this.actionError = message || this.translate.instant('permanentFile.downloadFailed');
          return;
        }
        const fileName = `OPENFRMT_${this.taxRegister || request.company_id}_${request.start_date}_${request.end_date}.zip`;
        saveBlob(blob, fileName);
        this.downloading = false;
      },
      error: async (error: HttpErrorResponse) => {
        this.downloading = false;
        if (error.status === 401) {
          return;
        }
        const body = error.error instanceof Blob ? await error.error.text() : error.error;
        this.actionError = readApiMessage(body, this.curentLang) || this.translate.instant('permanentFile.downloadFailed');
      },
    });
  }

  submitToAuthority(): void {
    if (!this.ensurePeriodReady()) {
      return;
    }
    if (this.oauthState === 'missing' || this.oauthState === 'expired') {
      this.oauthAttention = true;
      return;
    }

    const request = this.buildRequest(false);
    if (!request || this.submitting) {
      return;
    }

    this.submitting = true;
    this.actionError = '';
    this.oauthAttention = false;
    this.unistruct.submit(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: (job) => {
        this.submitting = false;
        const epoch = this.bumpEpoch(job.job_id);
        this.applyJob(job, epoch, true);
        this.noteLinkFromJob(job);
        this.expandedJobId = job.job_id;
        this.ensurePolling();
        this.scrollToJob(job.job_id);
      },
      error: (error: HttpErrorResponse) => {
        this.submitting = false;
        this.handleActionError(error, 'permanentFile.submitFailed');
      },
    });
  }

  connectCompany(): void {
    if (!this.selectedCompanyId || this.connecting) {
      return;
    }
    this.connecting = true;
    this.actionError = '';
    this.unistruct.authorizationUrl(this.selectedCompanyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: { url?: string }) => {
          this.connecting = false;
          if (!res?.url) {
            this.actionError = this.translate.instant('permanentFile.connectFailed');
            return;
          }
          const url = res.url;
          void Swal.fire({
            icon: 'info',
            title: this.translate.instant('permanentFile.redirecting'),
            timer: 1400,
            showConfirmButton: false,
          }).then(() => {
            window.location.href = url;
          });
        },
        error: (error: HttpErrorResponse) => {
          this.connecting = false;
          if (error.status === 401 && !isUnistructService401(error)) {
            return;
          }
          this.handleActionError(error, 'permanentFile.connectFailed');
        },
      });
  }

  loadJobs(companyId: number | null = this.selectedCompanyId): void {
    if (!companyId) {
      return;
    }
    const requestId = ++this.jobsRequest;
    this.pollGeneration += 1;
    this.jobsLoading = true;
    this.jobsError = '';
    this.unistruct.listJobs(companyId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (jobs) => {
        if (requestId !== this.jobsRequest) {
          return;
        }
        const localJobs = this.jobs.filter((job) =>
          (this.jobEpoch.get(job.job_id) ?? 0) > 0 && !jobs.some((item) => item.job_id === job.job_id)
        );
        this.jobs = [...localJobs, ...jobs];
        this.jobsLoading = false;
        if (!this.jobs.some((job) => job.job_id === this.expandedJobId)) {
          this.expandedJobId = this.jobs[0]?.job_id ?? null;
        }
        this.ensurePolling();
      },
      error: (error: HttpErrorResponse) => {
        if (requestId !== this.jobsRequest) {
          return;
        }
        this.jobsLoading = false;
        this.jobs = [];
        this.stopPolling();
        if (error.status === 401 && !isUnistructService401(error)) {
          return;
        }
        if (isUnistructService401(error)) {
          this.handleActionError(error, 'permanentFile.jobsFailed');
          return;
        }
        this.jobsError = readApiMessage(error.error, this.curentLang)
          || this.translate.instant('permanentFile.jobsFailed');
      },
    });
  }

  toggleJob(job: UnistructJob): void {
    this.expandedJobId = this.expandedJobId === job.job_id ? null : job.job_id;
  }

  retry(job: UnistructJob, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!job.ui?.can_retry || this.busyJobIds.has(job.job_id)) {
      return;
    }

    const epoch = this.bumpEpoch(job.job_id);
    this.busyJobIds.add(job.job_id);
    this.actionError = '';
    this.unistruct.retry(job.job_id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (updated) => {
        this.busyJobIds.delete(job.job_id);
        this.applyJob(updated, epoch, false);
        this.noteLinkFromJob(updated);
        this.expandedJobId = updated.job_id;
        this.ensurePolling();
      },
      error: (error: HttpErrorResponse) => {
        this.busyJobIds.delete(job.job_id);
        this.handleActionError(error, 'permanentFile.retryFailed');
      },
    });
  }

  isBusy(job: UnistructJob): boolean {
    return this.busyJobIds.has(job.job_id);
  }

  isLive(job: UnistructJob): boolean {
    return shouldPollUnistructJob(job);
  }

  needsRelink(job: UnistructJob): boolean {
    return job.user_status === 'FAILED_RETRYABLE' && !job.ui?.can_retry;
  }

  stepsFor(job: UnistructJob): UnistructStep[] {
    return buildUnistructSteps(job);
  }

  statusLabel(job: UnistructJob): string {
    const key = `permanentFile.statuses.${job.user_status}`;
    const translated = this.translate.instant(key);
    return translated === key ? job.user_status : translated;
  }

  statusClass(job: UnistructJob): string {
    switch (job.user_status) {
      case 'ACCEPTED':
        return 'is-accepted';
      case 'REJECTED':
        return 'is-rejected';
      case 'FAILED_RETRYABLE':
        return 'is-failed';
      case 'PROCESSING':
      case 'RECEIVED':
      case 'SENDING':
      case 'INI_UPLOADED':
      case 'BKM_UPLOADED':
        return 'is-progress';
      default:
        return 'is-neutral';
    }
  }

  formatPeriod(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) {
      return value;
    }
    return `${match[3]}/${match[2]}/${match[1]}`;
  }

  trackJob(_index: number, job: UnistructJob): number {
    return job.job_id;
  }

  private onCompanyChange(value: unknown): void {
    const companyId = readCompanyId(value);
    if (!companyId || companyId === this.selectedCompanyId) {
      return;
    }
    this.selectedCompanyId = companyId;
    this.actionError = '';
    this.linkDetail = '';
    this.oauthAttention = false;
    this.oauthState = 'unknown';
    this.jobs = [];
    this.expandedJobId = null;
    this.stopPolling();
    this.loadCompanyLink(companyId);
    this.loadJobs(companyId);
  }

  private loadCompanies(): void {
    this.service.GetAllCompaniesIncludingArchived()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.mastercompany = normalizeCompanyList(res);
        const preferred = readStorageNumber('companyDefault');
        const match = this.mastercompany.find((company) => Number(company?.company_id) === preferred);
        const chosen = match || (this.mastercompany.length === 1 ? this.mastercompany[0] : null);
        if (chosen?.company_id != null) {
          this.createZip.patchValue({ company_id: chosen.company_id });
        }
      });
  }

  private loadCompanyLink(companyId: number): void {
    const requestId = ++this.companyRequest;
    this.service.getCompanyInfoById(companyId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (requestId !== this.companyRequest || this.selectedCompanyId !== companyId) {
            return;
          }
          const company = res?.company_id != null ? res : (res?.data ?? res);
          this.taxRegister = String(company?.tax_register ?? '');
        },
        error: () => {
          if (requestId !== this.companyRequest || this.selectedCompanyId !== companyId) {
            return;
          }
          this.taxRegister = '';
        },
      });
  }

  private ensurePeriodReady(): boolean {
    this.createZip.markAllAsTouched();
    if (this.createZip.invalid) {
      this.actionError = this.createZip.hasError('range')
        ? this.translate.instant('permanentFile.invalidRange')
        : this.translate.instant('permanentFile.fillFields');
      return false;
    }
    return true;
  }

  private buildRequest(includeCreatedBy: boolean) {
    const companyId = readCompanyId(this.createZip.value.company_id);
    const merchantId = Number(this.merchantId);
    if (!companyId || this.merchantId == null) {
      this.actionError = this.translate.instant('permanentFile.fillFields');
      return null;
    }
    return {
      company_id: companyId,
      merchant_id: merchantId,
      start_date: String(this.createZip.value.start_date),
      end_date: String(this.createZip.value.end_date),
      lang: getDefaultReportLanguage(),
      ...(includeCreatedBy && this.userRegistId != null ? { created_by: Number(this.userRegistId) } : {}),
    };
  }

  private handleActionError(error: HttpErrorResponse, fallbackKey: string): void {
    if (error?.status === 401) {
      if (isUnistructService401(error)) {
        this.oauthState = 'missing';
        this.oauthAttention = true;
        this.linkDetail = readApiMessage(error.error, this.curentLang);
        this.actionError = '';
      }
      return;
    }
    if (error?.status === 404 || error?.status === 406) {
      this.oauthState = error.status === 406 ? 'expired' : 'missing';
      this.oauthAttention = true;
      this.linkDetail = readApiMessage(error.error, this.curentLang);
      this.actionError = '';
      return;
    }
    if (error?.status === 502) {
      this.actionError = readApiMessage(error.error, this.curentLang)
        || this.translate.instant('permanentFile.serviceUnavailable');
      return;
    }
    if (error?.status === 403) {
      const message = readApiMessage(error.error, this.curentLang);
      this.actionError = looksLikeHtmlForbidden(error.error) || !message
        ? this.translate.instant('permanentFile.gatewayBlocked')
        : message;
      return;
    }
    this.actionError = readApiMessage(error?.error, this.curentLang) || this.translate.instant(fallbackKey);
  }

  private noteLinkFromJob(job: UnistructJob): void {
    if (this.needsRelink(job)) {
      this.oauthState = 'expired';
      this.oauthAttention = true;
      return;
    }
    if (job.user_status !== 'FAILED_RETRYABLE') {
      this.oauthState = 'unknown';
      this.oauthAttention = false;
      this.linkDetail = '';
    }
  }

  private applyJob(job: UnistructJob, epoch: number, prepend: boolean): void {
    if ((this.jobEpoch.get(job.job_id) ?? 0) !== epoch) {
      return;
    }
    const index = this.jobs.findIndex((item) => item.job_id === job.job_id);
    if (index === -1) {
      this.jobs = [job, ...this.jobs];
      return;
    }
    const next = this.jobs.slice();
    next.splice(index, 1);
    this.jobs = prepend ? [job, ...next] : [...next.slice(0, index), job, ...next.slice(index)];
  }

  private bumpEpoch(jobId: number): number {
    const next = (this.jobEpoch.get(jobId) ?? 0) + 1;
    this.jobEpoch.set(jobId, next);
    return next;
  }

  private ensurePolling(): void {
    if (!this.jobs.some((job) => shouldPollUnistructJob(job))) {
      this.stopPolling();
      return;
    }
    if (this.pollTimer) {
      return;
    }
    this.pollTimer = setInterval(() => this.pollOnce(), this.pollMs);
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private pollOnce(): void {
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }
    const generation = this.pollGeneration;
    const live = this.jobs.filter((job) => shouldPollUnistructJob(job) && !this.busyJobIds.has(job.job_id));
    if (!live.length) {
      this.stopPolling();
      return;
    }
    const targets = live.filter((job) => !this.pollingIds.has(job.job_id));
    if (!targets.length) {
      return;
    }
    targets.forEach((job) => {
      const epoch = this.jobEpoch.get(job.job_id) ?? 0;
      this.pollingIds.add(job.job_id);
      this.unistruct.refreshStatus(job.job_id).pipe(takeUntil(this.destroy$)).subscribe({
        next: (updated) => {
          this.pollingIds.delete(job.job_id);
          if (generation !== this.pollGeneration) {
            return;
          }
          this.applyJob(updated, epoch, false);
          this.noteLinkFromJob(updated);
          this.ensurePolling();
        },
        error: (error: HttpErrorResponse) => {
          this.pollingIds.delete(job.job_id);
          if (error?.status === 401) {
            if (isUnistructService401(error)) {
              this.handleActionError(error, 'permanentFile.submitFailed');
            }
            this.stopPolling();
            return;
          }
          this.ensurePolling();
        },
      });
    });
  }

  private scrollToJob(jobId: number): void {
    setTimeout(() => {
      document.getElementById(`unistruct-job-${jobId}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 60);
  }

  private applyLayoutVersion(): void {
    this.layoutVersion = localStorage.getItem('layout_version');
    if (this.layoutVersion == null) {
      localStorage.setItem('layout_version', 'light-only');
      this.layoutVersion = 'light-only';
      return;
    }
    document.body.classList.add(this.layoutVersion === 'dark-only' ? 'dark-only' : 'light-only');
  }
}

function dateRangeValidator(group: AbstractControl): ValidationErrors | null {
  const start = group.get('start_date')?.value;
  const end = group.get('end_date')?.value;
  if (!start || !end) {
    return null;
  }
  return String(start) <= String(end) ? null : { range: true };
}

function normalizeCompanyList(response: any): any[] {
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  if (Array.isArray(response?.content)) {
    return response.content;
  }
  return [];
}

function readCompanyId(value: unknown): number | null {
  if (value && typeof value === 'object' && 'company_id' in value) {
    return readCompanyId((value as { company_id: unknown }).company_id);
  }
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function readStorageNumber(key: string): number | null {
  const raw = localStorage.getItem(key);
  if (raw == null || raw === '' || raw === 'null' || raw === 'undefined') {
    return null;
  }
  try {
    return readCompanyId(JSON.parse(raw));
  } catch {
    return readCompanyId(raw);
  }
}

function looksLikeHtmlForbidden(body: unknown): boolean {
  if (typeof body !== 'string') {
    return false;
  }
  const text = body.trim().toLowerCase();
  return text.startsWith('<') || text.includes('<html') || text.includes('forbidden');
}

async function isZipBlob(blob: Blob, contentType: string): Promise<boolean> {
  const type = contentType.toLowerCase();
  if (type.includes('zip') || type.includes('octet-stream')) {
    return true;
  }
  if (type.includes('json') || type.includes('text') || type.includes('html')) {
    return false;
  }
  const header = new Uint8Array(await blob.slice(0, 2).arrayBuffer());
  return header.length >= 2 && header[0] === 0x50 && header[1] === 0x4b;
}

function saveBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}
